import React, { useState, useEffect } from 'react';
import styles from './BookingsDashboard.module.css';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const BookingsDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' or 'today'
  const [centerFilter, setCenterFilter] = useState('all'); // 'all', 'Muzaffarpur', 'Sitamarhi'
  const [branchFilter, setBranchFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'confirmed', 'cancelled'
  const [selectedBooking, setSelectedBooking] = useState(null);

  const branchOptions = {
    'Muzaffarpur': ['Power House Chowk', 'Akharaghat'],
    'Sitamarhi': ['Dumra']
  };

  // Password Change State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwords, setPasswords] = useState({ oldPassword: '', newPassword: '' });

  const navigate = useNavigate();

  // Fetch all bookings
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://kidsworkrehab.onrender.com/api/bookings');
      const data = await response.json();

      if (data.success) {
        setBookings(data.data);
        setFilteredBookings(data.data);
      } else {
        setError('Failed to fetch bookings');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // Filter Logic (Same as before)
  useEffect(() => {
    let filtered = [...bookings];
    if (filter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.createdAt);
        return bookingDate >= today && bookingDate < tomorrow;
      });
    }
    if (centerFilter !== 'all') {
      filtered = filtered.filter(booking => booking.center === centerFilter);
    }
    if (branchFilter !== 'all') {
      filtered = filtered.filter(booking => booking.branch === branchFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }
    setFilteredBookings(filtered);
  }, [filter, centerFilter, branchFilter, statusFilter, bookings]);

  // Delete booking (Same as before)
  const handleDelete = (bookingId) => {
    toast((t) => (
      <div style={{ padding: '10px' }}>
        <p>Delete this booking?</p>
        <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
          <button onClick={() => toast.dismiss(t.id)} style={{ padding: '5px 10px', border: '1px solid #ccc', borderRadius: '4px' }}>Cancel</button>
          <button onClick={() => confirmDelete(bookingId, t.id)} style={{ padding: '5px 10px', background: 'red', color: 'white', border: 'none', borderRadius: '4px' }}>Delete</button>
        </div>
      </div>
    ));
  };

  const confirmDelete = async (bookingId, toastId) => {
    toast.dismiss(toastId);
    setLoading(true);
    try {
      const response = await fetch(`https://kidsworkrehab.onrender.com/api/bookings/${bookingId}`, { method: "DELETE" });
      const data = await response.json();
      if (data.success) {
        toast.success("Booking deleted successfully");
        fetchBookings();
        setSelectedBooking(null);
      } else {
        toast.error("Failed to delete booking");
      }
    } catch {
      toast.error("Error deleting booking");
    } finally {
      setLoading(false);
    }
  };

  // Update booking status
  const handleStatusUpdate = async (bookingId, newStatus) => {
    setLoading(true);
    try {
      const response = await fetch(`https://kidsworkrehab.onrender.com/api/bookings/${bookingId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Status updated successfully');
        fetchBookings();
        setSelectedBooking(null);
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      toast.error('Error updating status');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('https://kidsworkrehab.onrender.com/api/admin/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(passwords)
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Password updated successfully');
        setShowPasswordModal(false);
        setPasswords({ oldPassword: '', newPassword: '' });
      } else {
        toast.error(data.message || 'Failed to update password');
      }
    } catch (error) {
      toast.error('Server error');
    }
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
    };
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return styles.statusPending;
      case 'confirmed': return styles.statusConfirmed;
      case 'cancelled': return styles.statusCancelled;
      default: return '';
    }
  };

  if (loading && !bookings.length) return <div className={styles.container}><div className={styles.loading}>Loading bookings...</div></div>;
  if (error) return <div className={styles.container}><div className={styles.error}>{error}</div></div>;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Admin Dashboard</h1>
        <div className={styles.headerActions}>
          <button className={styles.refreshButton} onClick={fetchBookings}>
            🔄 Refresh
          </button>
          <button onClick={() => setShowPasswordModal(true)} className={styles.passwordButton}>
            🔑 Change Password
          </button>
          <button onClick={handleLogout} className={styles.logoutButton}>
            🚪 Logout
          </button>
        </div>
      </header>

      {/* Filters & Stats (Simplified for brevity, assuming styles handle layout) */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Date Filter:</label>
          <select
            className={styles.filterSelect}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Bookings</option>
            <option value="today">Today's Bookings</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Center:</label>
          <select
            className={styles.filterSelect}
            value={centerFilter}
            onChange={(e) => {
              setCenterFilter(e.target.value);
              setBranchFilter('all');
            }}
          >
            <option value="all">All Centers</option>
            <option value="Muzaffarpur">Muzaffarpur</option>
            <option value="Sitamarhi">Sitamarhi</option>
          </select>
        </div>

        {centerFilter !== 'all' && (
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Branch:</label>
            <select
              className={styles.filterSelect}
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
            >
              <option value="all">All Branches</option>
              {branchOptions[centerFilter]?.map(branch => (
                <option key={branch} value={branch}>{branch}</option>
              ))}
            </select>
          </div>
        )}

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Status:</label>
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.stats}>
        <div className={styles.statCard}><div className={styles.statNumber}>{filteredBookings.length}</div><div className={styles.statLabel}>Total Bookings</div></div>
        <div className={styles.statCard}><div className={styles.statNumber}>{filteredBookings.filter(b => b.status === 'pending').length}</div><div className={styles.statLabel}>Pending</div></div>
        <div className={styles.statCard}><div className={styles.statNumber}>{filteredBookings.filter(b => b.status === 'confirmed').length}</div><div className={styles.statLabel}>Confirmed</div></div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Appointment</th>
              <th>Center & Branch</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.length === 0 ? (
              <tr><td colSpan="7" className={styles.noData}>No bookings found</td></tr>
            ) : (
              filteredBookings.map((booking) => {
                const { date, time } = formatDateTime(booking.createdAt);
                const appointment = booking.appointmentDate ? formatDateTime(booking.appointmentDate).date : 'N/A';
                return (
                  <tr key={booking._id} className={styles.tableRow}>
                    <td><div className={styles.dateTimeCell}><div className={styles.date}>{date}</div><div className={styles.time}>{time}</div></div></td>
                    <td className={styles.nameCell}>{booking.name}</td>
                    <td>{booking.phone}</td>
                    <td><div className={styles.date} style={{ fontWeight: 500 }}>{appointment}</div></td>
                    <td><span className={styles.centerBadge}>{booking.center}</span><div style={{ fontSize: '0.8rem', marginTop: '4px', color: '#666' }}>{booking.branch}</div></td>
                    <td><span className={`${styles.statusBadge} ${getStatusClass(booking.status)}`}>{booking.status}</span></td>
                    <td>
                      <div className={styles.actions}>
                        <button className={styles.viewButton} onClick={() => setSelectedBooking(booking)}>View</button>
                        <button className={styles.deleteButton} onClick={() => handleDelete(booking._id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className={styles.modal} onClick={() => setSelectedBooking(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Booking Details</h2>
              <button className={styles.closeButton} onClick={() => setSelectedBooking(null)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Submission Date:</span>
                <span className={styles.detailValue}>
                  {formatDateTime(selectedBooking.createdAt).date} at {formatDateTime(selectedBooking.createdAt).time}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Appointment Date:</span>
                <span className={styles.detailValue}>
                  {selectedBooking.appointmentDate ? formatDateTime(selectedBooking.appointmentDate).date : 'N/A'}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Name:</span>
                <span className={styles.detailValue}>{selectedBooking.name}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Phone:</span>
                <span className={styles.detailValue}>{selectedBooking.phone}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Address:</span>
                <span className={styles.detailValue}>{selectedBooking.address}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Center:</span>
                <span className={styles.detailValue}>
                  <span className={styles.centerBadge}>{selectedBooking.center}</span>
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Branch:</span>
                <span className={styles.detailValue}>{selectedBooking.branch}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Therapies:</span>
                <div className={styles.therapiesList}>
                  {/* Handle therapies if array or string */}
                  {selectedBooking.therapies && Array.isArray(selectedBooking.therapies) ? (
                    selectedBooking.therapies.map((therapy, index) => (
                      <span key={index} className={styles.therapyTag}>
                        {therapy}
                      </span>
                    ))
                  ) : (
                    <span>{selectedBooking.therapies || 'None'}</span>
                  )}
                </div>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Current Status:</span>
                <span className={styles.detailValue}>
                  <span className={`${styles.statusBadge} ${getStatusClass(selectedBooking.status)}`}>
                    {selectedBooking.status}
                  </span>
                </span>
              </div>

              <div className={styles.statusUpdateSection}>
                <h3>Update Status:</h3>
                <div className={styles.statusButtons}>
                  <button onClick={() => handleStatusUpdate(selectedBooking._id, 'pending')} disabled={selectedBooking.status === 'pending'} className={`${styles.statusUpdateButton} ${styles.pendingBtn}`}>Pending</button>
                  <button onClick={() => handleStatusUpdate(selectedBooking._id, 'confirmed')} disabled={selectedBooking.status === 'confirmed'} className={`${styles.statusUpdateButton} ${styles.confirmedBtn}`}>Confirmed</button>
                  <button onClick={() => handleStatusUpdate(selectedBooking._id, 'cancelled')} disabled={selectedBooking.status === 'cancelled'} className={`${styles.statusUpdateButton} ${styles.cancelledBtn}`}>Cancelled</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className={styles.modal} onClick={() => setShowPasswordModal(false)}>
          <div className={`${styles.modalContent} !max-w-[500px]`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Change Password</h2>
              <button className={styles.closeButton} onClick={() => setShowPasswordModal(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleChangePassword}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Old Password</label>
                  <input
                    type="password"
                    required
                    className={styles.formInput}
                    value={passwords.oldPassword}
                    onChange={(e) => setPasswords({ ...passwords, oldPassword: e.target.value })}
                    placeholder="Enter current password"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>New Password</label>
                  <input
                    type="password"
                    required
                    className={styles.formInput}
                    value={passwords.newPassword}
                    onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                    placeholder="Enter new password"
                  />
                </div>
                <div className={styles.modalActions}>
                  <button type="button" onClick={() => setShowPasswordModal(false)} className={styles.cancelButton}>Cancel</button>
                  <button type="submit" className={styles.submitButton}>Update Password</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsDashboard;

